import React, { useState } from 'react';
import { MoveUp, MoveUpRight, MoveRight, MoveDownRight, MoveDown, MoveDownLeft, MoveLeft, MoveUpLeft, Circle } from 'lucide-react';
import { Button } from '../ui/button';

export const SEDAN_IMAGE = `${process.env.PUBLIC_URL || ''}/sedan-top.png`;

// The zones a photo can be assigned to. Exported for PhotoZoneFilter, which
// filters the Photos section by the same zones and draws the same car.
export const ZONES = [
  { id: 'front', label: 'Front', angle: 0, Icon: MoveDown },
  { id: 'front-right', label: 'Front Right', angle: 45, Icon: MoveDownLeft },
  { id: 'right', label: 'Right', angle: 90, Icon: MoveLeft },
  { id: 'rear-right', label: 'Rear Right', angle: 135, Icon: MoveUpLeft },
  { id: 'rear', label: 'Rear', angle: 180, Icon: MoveUp },
  { id: 'rear-left', label: 'Rear Left', angle: 225, Icon: MoveUpRight },
  { id: 'left', label: 'Left', angle: 270, Icon: MoveRight },
  { id: 'front-left', label: 'Front Left', angle: 315, Icon: MoveDownRight },
  { id: 'roof', label: 'Roof', angle: null, Icon: Circle },
  { id: 'underside', label: 'Underside', angle: null, Icon: null },
  { id: 'interior', label: 'Interior', angle: null, Icon: null },
  { id: 'rego-wof-ticket', label: 'WOF/Rego Ticket', angle: null, Icon: null },
  { id: 'document', label: 'Document', angle: null, Icon: null },
  { id: 'part', label: 'Part', angle: null, Icon: null },
  { id: 'odometer', label: 'Odometer', angle: null, Icon: null },
  { id: 'other', label: 'Other', angle: null, Icon: null }
];

export const ARROW_POSITIONS = {
  front: { top: '20%', left: '50%' },
  'front-right': { top: '26%', left: '74%' },
  right: { top: '50%', left: '80%' },
  'rear-right': { top: '74%', left: '74%' },
  rear: { top: '80%', left: '50%' },
  'rear-left': { top: '74%', left: '26%' },
  left: { top: '50%', left: '20%' },
  'front-left': { top: '26%', left: '26%' },
  roof: { top: '50%', left: '50%' }
};

export const CLICKABLE_AREAS = {
  front: { top: '15%', left: '35%', width: '30%', height: '20%' },
  'front-right': { top: '20%', left: '60%', width: '25%', height: '25%' },
  right: { top: '40%', left: '65%', width: '25%', height: '20%' },
  'rear-right': { top: '60%', left: '60%', width: '25%', height: '25%' },
  rear: { top: '65%', left: '35%', width: '30%', height: '20%' },
  'rear-left': { top: '60%', left: '15%', width: '25%', height: '25%' },
  left: { top: '40%', left: '10%', width: '25%', height: '20%' },
  'front-left': { top: '20%', left: '15%', width: '25%', height: '25%' },
  roof: { top: '40%', left: '40%', width: '20%', height: '20%' }
};

export const buildWedgeStyle = (angle, intensity, zoneId) => {
  if (!intensity) return { opacity: 0 };

  const isLeftOrRight = zoneId === 'left' || zoneId === 'right';
  const isCorner = zoneId.includes('-');
  let maskGradient;
  if (isLeftOrRight) {
    maskGradient = 'radial-gradient(circle at 50% 50%, rgba(0,0,0,0.95) 0%, rgba(0,0,0,0) 60%)';
  } else if (isCorner) {
    maskGradient = 'radial-gradient(circle at 50% 50%, rgba(0,0,0,0.95) 0%, rgba(0,0,0,0) 70%)';
  } else {
    maskGradient = 'radial-gradient(circle at 50% 50%, rgba(0,0,0,0.95) 0%, rgba(0,0,0,0) 80%)';
  }

  const start = angle - 22.5;
  const colour = `rgba(56, 189, 248, ${intensity})`;
  return {
    opacity: 1,
    background: `conic-gradient(from ${start}deg at 50% 50%, ${colour} 0deg 45deg, rgba(56, 189, 248, 0) 48deg 360deg)`,
    filter: `blur(${intensity > 0.5 ? 18 : 12}px)`,
    maskImage: maskGradient
  };
};

export const buildRoofGlowStyle = (intensity) => {
  if (!intensity) return { opacity: 0 };
  return {
    opacity: 1,
    background: `radial-gradient(circle at 50% 50%, rgba(56, 189, 248, ${intensity}) 0%, rgba(56, 189, 248, 0) 40%)`,
    filter: `blur(${intensity > 0.5 ? 16 : 10}px)`
  };
};

/**
 * ZonePicker — compact car birds-eye-view for selecting a single zone.
 * Used in DamageImageForm sidebar to assign an image to a section.
 */
const ZonePicker = ({ value = '', onChange }) => {
  const [hoveredZone, setHoveredZone] = useState(null);

  const handleClick = (zoneId) => {
    onChange?.(value === zoneId ? '' : zoneId);
  };

  return (
    <div className="group/picker">
      <div className="relative isolate w-full aspect-[3/4] overflow-visible">
        {/* Glow layer */}
        <div className="pointer-events-none absolute inset-[8%]">
          {ZONES.filter(z => z.Icon).map((zone) => {
            const isSelected = value === zone.id;
            const isHovered = hoveredZone === zone.id && !isSelected;
            const intensity = isSelected ? 0.7 : isHovered ? 0.35 : 0;

            if (zone.id === 'roof') {
              return (
                <div
                  key={`glow-${zone.id}`}
                  className="absolute inset-0 transition-opacity duration-200 ease-out"
                  style={buildRoofGlowStyle(intensity)}
                />
              );
            }
            return (
              <div
                key={`glow-${zone.id}`}
                className="absolute inset-0 transition-opacity duration-200 ease-out"
                style={buildWedgeStyle(zone.angle, intensity, zone.id)}
              />
            );
          })}
        </div>

        {/* Car image */}
        <div className="absolute inset-[8%] overflow-hidden bg-transparent flex items-center justify-center">
          <img
            src={SEDAN_IMAGE}
            alt=""
            className="pointer-events-none object-contain"
            style={{ width: '70%', height: '70%' }}
          />
        </div>

        {/* Clickable areas + arrows */}
        {ZONES.filter(z => z.Icon).map((zone) => {
          const isSelected = value === zone.id;
          const clickableArea = CLICKABLE_AREAS[zone.id];
          const arrowPosition = ARROW_POSITIONS[zone.id];
          const Icon = zone.Icon;
          return (
            <React.Fragment key={zone.id}>
              <button
                type="button"
                aria-label={zone.label}
                onClick={() => handleClick(zone.id)}
                onMouseEnter={() => setHoveredZone(zone.id)}
                onMouseLeave={() => setHoveredZone(null)}
                className="absolute cursor-pointer transition-opacity duration-200"
                style={{
                  top: clickableArea.top,
                  left: clickableArea.left,
                  width: clickableArea.width,
                  height: clickableArea.height,
                }}
              />
              <div
                className={`transition-all duration-200 ${
                  isSelected
                    ? 'text-sky-500 opacity-100'
                    : hoveredZone === zone.id
                      ? 'text-sky-500 opacity-100'
                      : 'text-slate-400 opacity-0 group-hover/picker:opacity-30'
                } pointer-events-none`}
                style={{
                  top: arrowPosition.top,
                  left: arrowPosition.left,
                  transform: 'translate(-50%, -50%)',
                  position: 'absolute',
                }}
              >
                <Icon size={20} strokeWidth={2.5} />
              </div>
            </React.Fragment>
          );
        })}
      </div>

      {/* Non-visual zones */}
      <div className="mt-2 grid grid-cols-2 gap-1.5">
        {ZONES.filter(z => !z.Icon).map((zone) => {
          const isSelected = value === zone.id;
          return (
            <Button
              key={zone.id}
              type="button"
              variant="outline"
              size="sm"
              onClick={() => handleClick(zone.id)}
              className={`flex-1 text-xs ${
                isSelected
                  ? 'bg-sky-100 border-sky-500 text-sky-700 hover:bg-sky-200'
                  : 'text-slate-600 hover:text-sky-600 hover:border-sky-400'
              }`}
            >
              {zone.label}
            </Button>
          );
        })}
      </div>
    </div>
  );
};

export default ZonePicker;
